import lang from '@/lang'
import type { PageContext, PageEntity } from '@ai-chat-widget-contracts/contracts'

interface EntityRoute {
  pattern: RegExp
  type: PageEntity['type']
  label: string
}

// Page area keys (e.g. "INVOICES", "SETTINGS/ACCOUNT") sent as `pageContext.module`
// and matched by contextual-help.json. Derived from the route on purpose: user
// rights are too coarse (several pages share one right group) and subscription
// modules describe plan features, not pages.
const toAreaMap = (areas: Record<string, string[]>) => new Map(
  Object.entries(areas).flatMap(([area, segments]) => segments.map(segment => [segment, area] as [string, string])),
)

const PAGE_AREAS = toAreaMap({
  DOCUMENT_PAYMENT: ['documentPayment'],
  DOCUMENT_SIGNATURE: ['documentSignature'],
  SUPPLIERS: ['suppliers', 'supplier'],
  CUSTOMERS: ['customers', 'customer'],
  ARTICLES: ['articles', 'article'],
  OFFERS: ['offers', 'offer'],
  ORDERS: ['orders', 'order'],
  DELIVERY_NOTES: ['deliverynotes', 'deliverynote'],
  INVOICES: [
    'invoices', 'invoice', 'manualcreditnotes', 'manualcreditnote', 'closinginvoices', 'closinginvoice',
    'depositinvoices', 'depositinvoice', 'partialinvoices', 'partialinvoice',
  ],
  RECURRING_INVOICES: ['recurringinvoices', 'recurringinvoice'],
  PROJECTS: ['projects', 'project'],
  TRACKED_TIMES: ['timetrackings', 'timetracking'],
  ACCOUNTING: ['banking', 'bookings'],
  EXPENSES: ['expenses', 'expense'],
  REVENUES: ['revenues', 'revenue'],
  ACCOUNTANT_EXPORT: ['document-export'],
  VAT_RETURN: ['vatReturn'],
  DOCUMENTS: ['documents', 'document'],
  'ACCOUNTING/REPORTING': ['reporting'],
  MEMBERS: ['users'],
  ECONOMIC_ASSETS: ['economicAssets', 'economicAsset'],
  CLIENTS: ['clients', 'client'],
  'SETTINGS/ACCOUNT': ['my-account'],
  TAX_FORMS: ['taxForms', 'taxReports'],
  PURCHASES: ['purchases', 'purchase'],
  LETTERS: ['letters', 'letter'],
  APPS: ['apps', 'app'],
})

const SETTINGS_AREAS = toAreaMap({
  'SETTINGS/PAY_CONDITION': ['payment-conditions'],
  'SETTINGS/ACCOUNT': ['myaccount'],
  'SETTINGS/COMPANY': ['mycompany', 'myoffice'],
  'SETTINGS/LETTER_PAPER': ['letter-paper'],
  'SETTINGS/CUSTOMER_CENTER': ['customer-center'],
  'SETTINGS/INTERFACES': ['interfaces'],
  'SETTINGS/MASTERDATA': ['masterdata'],
  'SETTINGS/CUSTOM_FIELDS': ['customFields'],
  'SETTINGS/MISCELLANEOUS': ['more-settings'],
  'SETTINGS/DELIVERY_CONDITION': ['delivery-conditions'],
  'SETTINGS/EVENT_LOG': ['event-log'],
  'SETTINGS/MAILBOX': ['mailbox'],
})

const resolvePageArea = (route: string): string => {
  const [first, second, third] = route.split('/').filter(Boolean)
  if (first !== 'settings') return PAGE_AREAS.get(first) ?? ''
  if (second === 'tax-settings' && third === 'chart-of-accounts') return 'ACCOUNTING/BANKING/CHART_OF_ACCOUNTS'
  return SETTINGS_AREAS.get(second) ?? ''
}

// Map each page area to the localized label shown in the navigation. Sourced
// from @/lang so product overrides (orgamax/meinbuero) are respected. Compound
// areas like "ACCOUNTING/REPORTING" are matched before their top-level segment.
const MODULE_LABELS: Record<string, string> = {
  CUSTOMERS: lang.menu.masterData.customers,
  SUPPLIERS: lang.menu.masterData.suppliers,
  ARTICLES: lang.menu.masterData.articles,
  CLIENTS: lang.menu.masterData.clients,
  INVOICES: lang.menu.disposal.invoices,
  RECURRING_INVOICES: lang.menu.disposal.recurringInvoices,
  OFFERS: lang.menu.disposal.offers,
  ORDERS: lang.menu.disposal.orders,
  DELIVERY_NOTES: lang.menu.disposal.deliveryNotes,
  PROJECTS: lang.menu.disposal.projects,
  TRACKED_TIMES: lang.menu.disposal.timetracking,
  EXPENSES: lang.menu.finances.expenses,
  REVENUES: lang.menu.finances.revenues,
  ACCOUNTING: lang.menu.booking.title,
  ACCOUNTANT_EXPORT: lang.menu.booking.documentExport,
  VAT_RETURN: lang.menu.booking.vatReturn,
  TAX_FORMS: lang.taxReports.title,
  ECONOMIC_ASSETS: lang.menu.booking.economicAssets,
  DOCUMENTS: lang.menu.documents,
  LETTERS: lang.menu.office.letters,
  PURCHASES: lang.menu.obtaining.purchases,
  APPS: lang.menu.apps.title,
  MEMBERS: lang.settingSectionTitles.user,
  SETTINGS: lang.menu.settings.title,
  'SETTINGS/ACCOUNT': lang.settings.pageTitles.myAccount,
  'SETTINGS/COMPANY': lang.settings.pageTitles.myCompany,
  'SETTINGS/LETTER_PAPER': lang.settings.pageTitles.letterPaper,
  'SETTINGS/CUSTOMER_CENTER': lang.settings.pageTitles.customerCenter,
  'SETTINGS/INTERFACES': lang.settings.pageTitles.interfaces,
  'SETTINGS/MASTERDATA': lang.settings.pageTitles.masterdata,
  'SETTINGS/CUSTOM_FIELDS': lang.settings.pageTitles.customFields,
  'SETTINGS/MISCELLANEOUS': lang.settings.pageTitles.more,
  'SETTINGS/EVENT_LOG': lang.settingSectionTitles.eventLog,
  'SETTINGS/MAILBOX': lang.settings.pageTitles.mailbox,
  'ACCOUNTING/REPORTING': lang.menu.booking.reporting,
}

// Routes without a module mapping (e.g. /dashboard, /todos, /import, /shop)
// still need a localized label for the chat context header. Sourced from
// @/lang so product overrides (orgamax/meinbuero) are respected.
const ROUTE_LABELS: Record<string, string> = {
  '/dashboard': lang.menu.dashboard,
  '/firstSteps': lang.firstSteps.title,
  '/todos': lang.menu.office.todos,
  '/import': lang.menu.masterData.dataImport,
  '/shop': lang.menu.marketPlace.title,
  '/cancellations': lang.transactionTypes.cancellation,
  '/creditnotes': lang.transactionTypes.creditNote,
  '/manualcreditnotes': lang.transactionTypes.creditNote,
  '/dunnings': lang.transactionTypes.dunning,
  '/depositinvoices': lang.transactionTypes.depositInvoice,
  '/partialinvoices': lang.partialInvoiceTitle,
  '/closinginvoices': lang.transactionTypes.closingInvoice,
  '/recurringinvoices': lang.menu.disposal.recurringInvoices,
  '/expenses': lang.menu.finances.expenses,
  '/revenues': lang.menu.finances.revenues,
}

const resolveRouteLabel = (route: string): string | undefined => {
  const normalized = route.split(/[?#]/)[0].replace(/\/$/, '') || '/'
  const prefix = Object.keys(ROUTE_LABELS)
    .sort((left, right) => right.length - left.length)
    .find(path => normalized === path || normalized.startsWith(`${path}/`))
  return prefix ? ROUTE_LABELS[prefix] : undefined
}

const resolveModuleLabel = (area: string): string | undefined => (
  MODULE_LABELS[area] ?? MODULE_LABELS[area.split('/')[0]]
)

// Keep this list aligned with the concrete routes registered in app/src/allRoutes.tsx.
// Only URL-derived identifiers are included; no entity data is loaded in Phase 1.
const entityRoutes: EntityRoute[] = [
  { pattern: /^\/invoices\/(?:edit|send)\/([^/]+)(?:\/[^/]+)?$/, type: 'invoice', label: 'Rechnung' },
  { pattern: /^\/invoices\/(?!(?:new|edit|send)(?:\/|$))([^/]+)(?:\/[^/]+)?$/, type: 'invoice', label: 'Rechnung' },
  { pattern: /^\/offers\/(?:edit|send)\/([^/]+)$/, type: 'offer', label: 'Angebot' },
  { pattern: /^\/offers\/(?!(?:new|edit|send)(?:\/|$))([^/]+)(?:\/[^/]+)?$/, type: 'offer', label: 'Angebot' },
  { pattern: /^\/orders\/(?:edit|send)\/([^/]+)$/, type: 'order', label: 'Auftrag' },
  { pattern: /^\/orders\/(?!(?:new|edit|send)(?:\/|$))([^/]+)$/, type: 'order', label: 'Auftrag' },
  { pattern: /^\/customers\/edit\/([^/]+)(?:\/[^/]+)?$/, type: 'customer', label: 'Kunde' },
  { pattern: /^\/customers\/(?!(?:new|edit)(?:\/|$))([^/]+)$/, type: 'customer', label: 'Kunde' },
  { pattern: /^\/articles\/edit\/([^/]+)(?:\/[^/]+)?$/, type: 'article', label: 'Artikel' },
  { pattern: /^\/articles\/(?!(?:new|edit)(?:\/|$))([^/]+)$/, type: 'article', label: 'Artikel' },
]

const resolveEntity = (route: string): PageEntity | undefined => {
  for (const entityRoute of entityRoutes) {
    const match = route.match(entityRoute.pattern)
    if (match) {
      return {
        type: entityRoute.type,
        id: match[1],
        label: entityRoute.label,
      }
    }
  }
  return undefined
}

export const resolvePageContext = (route = window.location.pathname): PageContext => {
  const normalizedRoute = route.split(/[?#]/)[0].replace(/\/$/, '') || '/'
  const area = resolvePageArea(normalizedRoute)
  const entity = resolveEntity(normalizedRoute)
  const moduleLabel = resolveRouteLabel(normalizedRoute) ?? resolveModuleLabel(area)
  return {
    module: area,
    ...(moduleLabel ? { moduleLabel } : {}),
    route,
    ...(entity ? { entity } : {}),
  }
}
